import { useState, type ReactNode } from 'react';
import { DemMasterDataPage } from '../../pages/dem/DemMasterDataPage';
import { useAdminHubContext } from '../../contexts/AdminHubContext';
import { canManageDemConfigInAdminHub } from '../../lib/adminHubPermissions';
import { canRevealDemIntegrationSecrets } from '../../lib/demSettingsPermissions';
import { archiveOldDemOrders } from '../../lib/demSettings';

function SyncCategory({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="unified-users-dept">
      <header className="unified-users-dept__head">
        <h3 className="unified-users-dept__title">{title}</h3>
      </header>
      {children}
    </section>
  );
}

export function SynchronizeAdminPanel() {
  const hubCtx = useAdminHubContext();
  const actorRole = hubCtx?.actorRole ?? 'user';
  const canDem = canManageDemConfigInAdminHub(actorRole);
  const canArchive = canRevealDemIntegrationSecrets(actorRole);
  const [archiving, setArchiving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canDem) {
    return <p className="settings-empty">You cannot manage synchronize settings.</p>;
  }

  const handleArchive = async () => {
    if (
      !window.confirm(
        'Mark Processed orders older than 60 days as Archived?\n\nCancelled/voided orders are left unchanged.\n\nContinue?',
      )
    ) {
      return;
    }

    setArchiving(true);
    setError(null);
    setStatusMessage(null);
    try {
      await archiveOldDemOrders();
      setStatusMessage('Old orders archived successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive orders.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <section className="card settings-panel synchronize-admin">
      <div className="settings-panel__head unified-users-panel__head">
        <div className="unified-users-panel__title-row">
          <h2 className="settings-panel__title">Synchronize</h2>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {statusMessage ? (
        <p className="dem-uid-status dem-uid-status--ok" role="status">
          {statusMessage}
        </p>
      ) : null}

      <div className="unified-users-dept-list">
        <SyncCategory title="Master Data">
          <DemMasterDataPage embedded />
        </SyncCategory>

        <SyncCategory title="Maintenance">
          <div className="synchronize-admin__maintenance">
            <button
              type="button"
              className="btn-danger-outline btn-sm"
              disabled={archiving || !canArchive}
              onClick={() => void handleArchive()}
            >
              {archiving ? 'Archiving…' : 'Archive old orders'}
            </button>
          </div>
        </SyncCategory>
      </div>
    </section>
  );
}
